import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { httpClient } from 'common/services';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { sipSettings } from 'features/sip/config';
import { setUserEmail } from 'features/user/store';
import { selectIsInCallerSession } from 'features/caller-session/store';
import { shouldSuppressOidcErrorNotification } from 'features/auth/helpers';
import {
  tokenNotSetOnHeaders,
  tokenSetOnHeaders,
} from 'features/app/store/app/appSlice';
import { resetMultiMode } from 'features/multi-mode/store';
import { ModeStorageService } from 'features/multi-mode/services';
import { showNotification } from 'features/notification/store';
import { PageRoute } from 'features/router/routes';

export const useAuthorization = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isInCallerSession = useAppSelector(selectIsInCallerSession);
  const hasHandledCurrentErrorRef = useRef(false);
  const {
    events,
    user,
    error,
    isLoading: isAuthLoading,
    isAuthenticated,
    signinRedirect,
  } = useAuth();

  const setJwtToken = (token: string) => {
    sipSettings.update({ authorization_jwt: `Bearer ${token}` });
    httpClient.setHeader('Authorization', token);
    dispatch(tokenSetOnHeaders());
  };

  useEffect(() => {
    return events.addSilentRenewError(() => {
      if (!isInCallerSession) {
        signinRedirect();
      }
      dispatch(tokenNotSetOnHeaders());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInCallerSession]);

  useEffect(() => {
    if (
      !isAuthLoading &&
      !isAuthenticated &&
      !isInCallerSession &&
      !error // Don't auto-redirect if there's an authentication error
    ) {
      signinRedirect();
      dispatch(tokenNotSetOnHeaders());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAuthLoading, isInCallerSession, error]);

  // Handle authentication errors by redirecting to main page (VRS/VRI selection)
  useEffect(() => {
    if (
      error &&
      !isInCallerSession &&
      !isAuthenticated &&
      !hasHandledCurrentErrorRef.current
    ) {
      // Set the flag immediately before starting side effects to prevent race condition
      hasHandledCurrentErrorRef.current = true;

      // Show error notification to user
      if (!shouldSuppressOidcErrorNotification(error)) {
        dispatch(
          showNotification({
            severity: 'error',
            title: t('auth.signInAgain'),
          })
        );
      }
      // Clear the selected mode so user sees mode selection instead of auth again
      dispatch(resetMultiMode());
      // Also clear mode from localStorage to prevent auto-selection
      ModeStorageService.clear();
      // Navigate to main page
      navigate(PageRoute.MAIN);
    }
  }, [dispatch, error, isAuthenticated, isInCallerSession, navigate, t]);

  // Reset the error handling flag when error is cleared or user becomes authenticated
  useEffect(() => {
    if (!error || isAuthenticated) {
      hasHandledCurrentErrorRef.current = false;
    }
  }, [error, isAuthenticated]);

  useEffect(() => {
    const token = user?.id_token;
    if (!token) {
      return;
    }

    setJwtToken(token);
    dispatch(setUserEmail(user.profile.email!));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return user?.id_token ?? null;
};
